import * as React from 'react'
import { ReactElement, useContext, useMemo } from 'react'
import CommandDialog from '../Command/CommandDialog'
import { ActionButton, DialogFooter, PrimaryButton, Selection } from '@fluentui/react'
import ProcessEmailForm, { EmailProcessingAction } from './ProcessEmailForm'
import useUser from '../../../hooks/useUser'
import useEntity from '../../../hooks/useEntity'
import { Message } from '@microsoft/microsoft-graph-types'
import { Destination } from '../../../context/AxiosContext'
import { SelectableEmail } from '../../../hooks/email/useProcessEmail'
import { SelectionMode } from '@fluentui/react/lib/Selection'
import { useHistory, useLocation } from 'react-router-dom'
import { IBackgroundLocationState } from '../../../config/appConfig'
import { useBoolean } from '@fluentui/react-hooks'
import { mapMessages } from '../../Email/EmailSearchSelectList'
import AuthorizationContext from '../../../context/AuthorizationContext'

export type EmailFilingSuggestionDto = {
    messageId: string
    userEmail: string
}

export default function EmailFilingSuggestionDialog(): ReactElement {
    const { host: currentHost } = useContext(AuthorizationContext)
    const { currentMailbox, currentEmail } = useUser()
    const location = useLocation<IBackgroundLocationState<EmailFilingSuggestionDto>>()
    const history = useHistory<IBackgroundLocationState>()
    const [hideDialog, { setTrue: setHideDialog }] = useBoolean(false)
    const setHideDialogAndReturn = () => {
        setHideDialog()
        history.push(location.state?.background?.pathname || '/')
    }

    const [message] = useEntity<Message>(
        (itemId: string) => ({
            method: 'GET',
            url: `/messages/${itemId}`,
            baseURL: Destination.Email,
            headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
            params: {
                $select: 'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,receivedDateTime',
            },
        }),
        location.state?.data?.messageId,
    )

    const selection: Selection<SelectableEmail> = useMemo(() => {
        if (message.loaded) {
            const select = new Selection<SelectableEmail>({
                items: mapMessages([message.data], currentEmail, currentEmail, currentHost, Destination.Email),
                onSelectionChanged: () => {},
                getKey: item => item.id,
                selectionMode: SelectionMode.single,
            })
            select.setIndexSelected(0, true, true)
            return select
        }
        return new Selection<SelectableEmail>()
    }, [message])

    if (!selection || selection.count === 0) {
        return <></>
    }

    return (
        <CommandDialog dialogContentProps={{ title: 'E-mail handmatig verwerken' }} hidden={hideDialog} onDismiss={setHideDialogAndReturn}>
            {() => (
                <ProcessEmailForm
                    emailMailbox={currentMailbox}
                    onDismiss={setHideDialogAndReturn}
                    action={EmailProcessingAction.File}
                    selection={selection}
                    isLoading={message.loading}
                    setPreventDefaultEvent={() => {}}
                    reset={() => {}}
                >
                    <DialogFooter>
                        <ActionButton text="Annuleren" onClick={setHideDialogAndReturn} />
                        <PrimaryButton text="Verwerken" type="submit" />
                    </DialogFooter>
                </ProcessEmailForm>
            )}
        </CommandDialog>
    )
}
